import React, { useEffect, useState } from "react";
import "./newform.css";
import AddPlayerProfileModal from "../../AddPlayerProfileModal/AddPlayerProfileModal";
import { usePlayerAccount } from "../../../APIContext/PlayerAccountContext";
import { useNewUser } from "../../../APIContext/NewUserContext";
import { useSummerCamp } from "../../../APIContext/summerCampContext";

const SummerCampMultiPlayerForm = ({ offeringId, summercampList }) => {
  const [playerCount, setPlayerCount] = useState(1);
  const [showModalAdd, setShowModalAdd] = useState(false);
  const [price, setPrice] = useState(null);
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const [players, setPlayers] = useState([
    {
      internalId: Math.random(),
      player_id: "",
      medical_condition: "",
      week_ids: [],
      maxWeeks: null,
    },
  ]);

  const { fetchPlayers, players: totalplayer } = usePlayerAccount();
  const { calculateSummerCampPrice } = useNewUser();
  const { addToCartOffering } = useSummerCamp();

  const [weekCapacityMap, setWeekCapacityMap] = useState({});

  const weeksOptions =
    summercampList?.find((item) => String(item.id) === String(offeringId))
      ?.weeks || [];

  useEffect(() => {
    fetchPlayers();
  }, [fetchPlayers]);

  /* ---------------- AUTO PRICE ---------------- */
  useEffect(() => {
    const autoCalculatePrice = async () => {
      const hasWeeks = players.some((p) => p.week_ids.length > 0);
      if (!hasWeeks) {
        setPrice(null);
        return;
      }

      setIsCalculatingPrice(true);
      try {
        const weeksParam = players.map((p) => p.week_ids.length).join(",");
        const result = await calculateSummerCampPrice({ weeks: weeksParam });
        if (result) setPrice(result);
      } catch {
        setValidationErrors(["Price calculation failed."]);
      } finally {
        setIsCalculatingPrice(false);
      }
    };

    autoCalculatePrice();
  }, [players, calculateSummerCampPrice]);

  useEffect(() => {
    const offering = summercampList?.find(
      (item) => String(item.id) === String(offeringId),
    );

    if (!offering) return;

    const map = {};
    offering.weeks.forEach((w) => {
      map[w.id] = w.remaining_capacity;
    });

    setWeekCapacityMap(map);
  }, [summercampList, offeringId]);

  /* ---------------- PLAYER COUNT ---------------- */
  const handlePlayerCountChange = (e) => {
    const count = parseInt(e.target.value);
    setPlayerCount(count);

    setPlayers((prev) => {
      let updated = [...prev];
      if (count > prev.length) {
        for (let i = prev.length; i < count; i++) {
          updated.push({
            internalId: Math.random(),
            player_id: "",
            medical_condition: "",
            week_ids: [],
            maxWeeks: null,
          });
        }
      } else {
        updated = updated.slice(0, count);
      }
      return updated;
    });
  };

  /* ---------------- WEEK LIMIT LOGIC ---------------- */
  // const handleWeekLimitChange = (playerIndex, numWeeks) => {
  //   const limit = parseInt(numWeeks);
  //   if (isNaN(limit)) return;

  //   setPlayers((prev) => {
  //     const newPlayers = [...prev];
  //     let updatedWeeks = [];

  //     if (limit === weeksOptions?.length) {
  //       updatedWeeks = weeksOptions.slice(0, weeksOptions?.length).map((w) => Number(w.id));
  //     }

  //     newPlayers[playerIndex] = {
  //       ...newPlayers[playerIndex],
  //       maxWeeks: limit,
  //       week_ids: updatedWeeks,
  //     };

  //     return newPlayers;
  //   });
  // };

  const handleWeekLimitChange = (playerIndex, numWeeks) => {
    const limit = parseInt(numWeeks);
    if (isNaN(limit)) return;

    setPlayers((prevPlayers) => {
      const newPlayers = [...prevPlayers];
      const player = newPlayers[playerIndex];

      const previousWeeks = player.week_ids || [];
      let newWeeks = [];

      // ⭐ Auto-select all weeks only if limit == total weeks
      if (limit === weeksOptions.length) {
        newWeeks = weeksOptions.map((w) => Number(w.id));
      }

      // 🔁 RESTORE capacity for previously selected weeks
      setWeekCapacityMap((prev) => {
        const updated = { ...prev };

        previousWeeks.forEach((weekId) => {
          updated[weekId] = (updated[weekId] ?? 0) + 1;
        });

        // 🔻 REDUCE capacity for newly selected weeks
        newWeeks.forEach((weekId) => {
          updated[weekId] = (updated[weekId] ?? 0) - 1;
        });

        return updated;
      });

      newPlayers[playerIndex] = {
        ...player,
        maxWeeks: limit,
        week_ids: newWeeks,
      };

      return newPlayers;
    });
  };

  /* ---------------- PLAYER SELECT ---------------- */
  const handlePlayerSelect = (index, playerId) => {
    setPlayers((prev) => {
      const newPlayers = [...prev];
      newPlayers[index].player_id = playerId;
      return newPlayers;
    });
  };

  const handleMedicalChange = (index, value) => {
    setPlayers((prev) => {
      const newPlayers = [...prev];
      newPlayers[index].medical_condition = value;
      return newPlayers;
    });
  };

  /* ---------------- WEEK TOGGLE ---------------- */
  // const toggleWeek = (playerIndex, weekId) => {
  //   const idToToggle = Number(weekId);

  //   setPlayers((prev) => {
  //     const newPlayers = [...prev];
  //     const player = newPlayers[playerIndex];
  //     const currentIds = player.week_ids;
  //     const limit = player.maxWeeks;

  //     if (!limit) return prev;

  //     if (currentIds.includes(idToToggle)) {
  //       player.week_ids = currentIds.filter((id) => id !== idToToggle);
  //     } else {
  //       if (currentIds.length >= limit) return prev;
  //       player.week_ids = [...currentIds, idToToggle];
  //     }

  //     return newPlayers;
  //   });
  // };

  const toggleWeek = (playerIndex, weekId) => {
    const id = Number(weekId);

    setPlayers((prevPlayers) => {
      const newPlayers = [...prevPlayers];
      const player = newPlayers[playerIndex];
      const currentIds = player.week_ids;
      const limit = player.maxWeeks;

      if (!limit) return prevPlayers;

      const isSelected = currentIds.includes(id);

      if (!isSelected && weekCapacityMap[id] <= 0) {
        return prevPlayers;
      }

      let updatedWeeks;

      if (isSelected) {
        updatedWeeks = currentIds.filter((w) => w !== id);

        setWeekCapacityMap((prev) => ({
          ...prev,
          [id]: prev[id] + 1,
        }));
      } else {
        if (currentIds.length >= limit) return prevPlayers;

        updatedWeeks = [...currentIds, id];

        setWeekCapacityMap((prev) => ({
          ...prev,
          [id]: prev[id] - 1,
        }));
      }

      newPlayers[playerIndex] = {
        ...player,
        week_ids: updatedWeeks,
      };

      return newPlayers;
    });
  };

  /* ---------------- VALIDATION ---------------- */
  const areWeeksValid = players.every(
    (p) => p.maxWeeks && p.week_ids.length === p.maxWeeks,
  );

  /* ---------------- ADD TO CART ---------------- */
  const handleAddToCart = async () => {
    if (!price || !areWeeksValid) return;

    setIsAddingToCart(true);
    try {
      const payload = {
        offering_id: parseInt(offeringId),
        total_price: price.total_amount,
        players: players.map(({ internalId, ...rest }) => rest),
      };

      const result = await addToCartOffering(payload);
      if (result) window.location.reload();
    } catch {
      setValidationErrors(["Add to cart failed."]);
    } finally {
      setIsAddingToCart(false);
    }
  };

  const weeksArray = [...Array(weeksOptions?.length || 0)].map((_, i) => i + 1);

  return (
    <>
      <div
        style={{ filter: "brightness(1.28)" }}
        className="form-wrapper new-user bg-custom"
      >
        <div className="form-card main-card-smooth">
          <div className="form-title text-clip">SUMMER CAMP REGISTRATION</div>

          {validationErrors.length > 0 && (
            <div className="error-banner">
              <ul>
                {validationErrors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="form-group">
            <label className="label-bold text-clip">How Many Players?*</label>
            <div className="top-row-controls">
              <select
                className="form-select smooth-input"
                value={playerCount}
                onChange={handlePlayerCountChange}
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>
                    {n} Player{n > 1 ? "s" : ""}
                  </option>
                ))}
              </select>

              <button
                className="add-new-player-btn smooth-btn"
                onClick={() => setShowModalAdd(true)}
              >
                Add Player
              </button>
            </div>
          </div>

          {players.map((player, index) => (
            <div
              key={player.internalId}
              className="player-section-grey smooth-container bg-custom"
            >
              <div className="label-bold text-clip">
                Player {index + 1} Details
              </div>

              <select
                className="form-select smooth-input"
                value={player.player_id}
                onChange={(e) => handlePlayerSelect(index, e.target.value)}
              >
                <option value="">Select Player*</option>
                {totalplayer?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>

              <textarea
                className="form-input smooth-input"
                placeholder="Medical Condition*"
                value={player.medical_condition}
                onChange={(e) => handleMedicalChange(index, e.target.value)}
              />

              <div
                className="quick-select-wrapper"
                style={{ marginTop: "15px" }}
              >
                <label className="quick-select-label text-clip">
                  How many weeks do you want to select?
                </label>
                <select
                  className="form-select smooth-input"
                  defaultValue=""
                  onChange={(e) => handleWeekLimitChange(index, e.target.value)}
                >
                  <option value="" disabled>
                    Choose number of week
                  </option>
                  {weeksArray.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>

              {player.maxWeeks && (
                <div
                  className="quick-select-wrapper"
                  style={{
                    marginTop: "10px",
                    fontSize: "13px",
                    color:
                      player.week_ids.length === player.maxWeeks
                        ? "#2e7d32"
                        : "#c62828",
                  }}
                >
                  {player.week_ids.length === player.maxWeeks ? (
                    <>
                      ✔ You have selected {player.maxWeeks} of {player.maxWeeks}{" "}
                      weeks
                    </>
                  ) : (
                    <>
                      Note: Please select {player.maxWeeks} weeks to continue.
                      You have selected {player.week_ids.length} weeks.{" "}
                    </>
                  )}
                </div>
              )}

              <div className="weeks-section">
                <div
                  className="label-bold text-clip"
                  style={{ margin: "15px 0" }}
                >
                  Choose Summer Camp Weeks*
                </div>

                <div className="big-checkbox-list">
                  {/* {weeksOptions.map((week) => {
                  const isChecked = player.week_ids.includes(Number(week.id));
                  const isDisabled = !player.maxWeeks ;

                  return (
                    <label key={week.id} className={`big-checkbox-row ${isDisabled ? "disabled-week" : ""}`}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={isDisabled}
                        onChange={() => toggleWeek(index, week.id)}
                        className="hidden-checkbox"
                      />
                      <div className={`checkbox-box ${isChecked ? "checked" : ""}`}>
                        {isChecked && <span>✔</span>}
                      </div>
                      <span className="checkbox-label-text">Week {week.week_number}</span>
                    </label>
                  );
                })} */}

                  {weeksOptions.map((week) => {
                    const isChecked = player.week_ids.includes(Number(week.id));
                    const isDisabled =
                      !player.maxWeeks ||
                      (!isChecked && weekCapacityMap[week.id] === 0);

                    return (
                      <label
                        key={week.id}
                        className={`big-checkbox-row ${isDisabled ? "disabled-week" : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={isDisabled}
                          onChange={() => toggleWeek(index, week.id)}
                          className="hidden-checkbox"
                        />

                        <div
                          className={`checkbox-box ${isChecked ? "checked" : ""}`}
                          style={{
                            opacity: isDisabled ? 0.4 : 1,
                            cursor: isDisabled ? "not-allowed" : "pointer",
                          }}
                        >
                          {isChecked && <span>✔</span>}
                        </div>

                        <span className="checkbox-label-text">
                          <span className="text-clip">
                            Week {week.week_number}
                          </span>
                          {weekCapacityMap[week.id] < 10 && (
                            <small
                              style={{ color: "#d01515", marginLeft: "6px" }}
                            >
                              (Only left {weekCapacityMap[week.id]} )
                            </small>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}

          <div className="form-footer">
            {isCalculatingPrice && (
              <div className="total-row text-clip">Calculating price...</div>
            )}

            {price && !isCalculatingPrice && (
              <div className="total-row">
                <span className="total-label text-clip">TOTAL</span>
                <span className="total-amount text-clip">
                  ${price.total_amount?.toFixed(2)}
                </span>
              </div>
            )}

            <button
              className="add-cart-btn smooth-btn"
              onClick={handleAddToCart}
              disabled={
                !price || isCalculatingPrice || isAddingToCart || !areWeeksValid
              }
            >
              {isAddingToCart
                ? "Adding..."
                : isCalculatingPrice
                  ? "Calculating..."
                  : "Add To Cart"}
            </button>
          </div>
        </div>
      </div>
      {showModalAdd && (
        <AddPlayerProfileModal onClose={() => setShowModalAdd(false)} />
      )}
    </>
  );
};

export default SummerCampMultiPlayerForm;
